/**
 * Rules for the Adjust inventory form, kept apart from the component so they can be tested
 * without rendering anything. The server enforces the same rules (stock-movements module);
 * this copy exists so the user hears about a problem before saving, not after.
 */
import type { CountMethod, MovementPayload, MovementType } from '../../../services/stockMovementService';
import type { SiteWithLocations, StorageBin, StorageLocation } from '../types';
import { binMaterialLabel } from './siteDrillThrough';

export const MOVEMENT_TABS: { value: MovementType; label: string }[] = [
  { value: 'CONSUME', label: 'Consume' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'RECEIPT', label: 'Receipt' },
  { value: 'COUNT', label: 'Count' },
  { value: 'DISPOSAL', label: 'Disposal' },
  { value: 'RETURN', label: 'Return' },
];

export const COUNT_METHOD_OPTIONS: { value: CountMethod; label: string }[] = [
  { value: 'DRONE', label: 'Drone fly-over' },
  { value: 'DIP', label: 'Dip reading' },
  { value: 'PHYSICAL', label: 'Physical count' },
];

/** What each type asks for. The modal renders from this, so a type cannot show a stray field. */
export interface TypeSpec {
  /** Label for the bin the stock leaves (or, for a count, the bin counted). Null: none. */
  fromLabel: string | null;
  toLabel: string | null;
  quantity: boolean;
  task: 'required' | 'optional' | null;
  vendor: boolean;
  unitCost: boolean;
  method: boolean;
  counted: boolean;
  costPanel: boolean;
  /** The stock leaves a bin, so the quantity cannot exceed what the bin holds. */
  drawsDown: boolean;
}

const NONE: TypeSpec = {
  fromLabel: null,
  toLabel: null,
  quantity: false,
  task: null,
  vendor: false,
  unitCost: false,
  method: false,
  counted: false,
  costPanel: false,
  drawsDown: false,
};

export const TYPE_SPEC: Record<MovementType, TypeSpec> = {
  CONSUME: { ...NONE, fromLabel: 'From location', quantity: true, task: 'required', drawsDown: true },
  TRANSFER: {
    ...NONE,
    fromLabel: 'From location',
    toLabel: 'To location',
    quantity: true,
    costPanel: true,
    drawsDown: true,
  },
  RECEIPT: { ...NONE, toLabel: 'To location', quantity: true, vendor: true, unitCost: true },
  COUNT: { ...NONE, fromLabel: 'Location counted', method: true, counted: true },
  DISPOSAL: { ...NONE, fromLabel: 'From location', quantity: true, drawsDown: true },
  RETURN: { ...NONE, toLabel: 'To location', quantity: true, task: 'optional' },
};

/** Every field is a string because that is what the inputs hold; parsing happens on submit. */
export interface AdjustForm {
  /** Where stock leaves, or the bin counted. */
  fromBin: string;
  toBin: string;
  material: string;
  quantity: string;
  task: string;
  vendor: string;
  unitCost: string;
  method: CountMethod;
  counted: string;
  reason: string;
}

export const EMPTY_FORM: AdjustForm = {
  fromBin: '',
  toBin: '',
  material: '',
  quantity: '',
  task: '',
  vendor: '',
  unitCost: '',
  method: 'PHYSICAL',
  counted: '',
  reason: '',
};

/** A bin together with where it sits, flattened so a form can offer it in one list. */
export interface SiteBin {
  bin: StorageBin;
  location: StorageLocation;
  /** "North stockpile · Bin 1". A bin code alone is not unique within a site. */
  label: string;
}

export function flattenBins(site: Pick<SiteWithLocations, 'locations'>): SiteBin[] {
  return site.locations.flatMap((location) =>
    location.bins.map((bin) => ({ bin, location, label: `${location.name} · ${bin.code}` })),
  );
}

export function formatQuantity(value: number, unit: string | null | undefined): string {
  const text = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 3 }).format(value);
  return unit ? `${text} ${unit}` : text;
}

export function formatCost(value: number | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined) return '—';
  const money = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);
  return unit ? `${money} /${unit}` : money;
}

/** A material as the picker lists it. */
export interface MaterialChoice {
  id: string;
  label: string;
  /** Group heading, e.g. "AGGREGATE - SAND". */
  group: string;
  unit: string | null;
  /** Shown at the right of the row. Null for a catalogue material nobody holds here. */
  onHand: number | null;
}

const NO_GROUP = 'Other';

/** Materials held in this site's bins, with the total on hand across them. */
export function siteMaterialChoices(bins: SiteBin[], opts: { inStockOnly: boolean }): MaterialChoice[] {
  const byId = new Map<string, MaterialChoice>();
  for (const { bin } of bins) {
    if (!bin.material) continue;
    if (opts.inStockOnly && bin.quantity <= 0) continue;
    const existing = byId.get(bin.material.id);
    if (existing) {
      existing.onHand = (existing.onHand ?? 0) + bin.quantity;
      continue;
    }
    byId.set(bin.material.id, {
      id: bin.material.id,
      label: binMaterialLabel(bin) ?? 'Unknown material',
      group: bin.material.type?.trim() || NO_GROUP,
      unit: bin.material.unitOfMeasure,
      onHand: bin.quantity,
    });
  }
  return Array.from(byId.values()).sort(
    (a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
}

/** Group choices under their heading, keeping their order. */
export function groupChoices(choices: MaterialChoice[]): { group: string; items: MaterialChoice[] }[] {
  const groups: { group: string; items: MaterialChoice[] }[] = [];
  for (const choice of choices) {
    const last = groups[groups.length - 1];
    if (last && last.group === choice.group) last.items.push(choice);
    else groups.push({ group: choice.group, items: [choice] });
  }
  return groups;
}

/**
 * Bins stock can be taken from: they hold a material, and (optionally) this one. A bin the ledger
 * shows at or below zero is still offered - the ledger can be behind what is on the ground, and
 * refusing the record would only lose it. The draw is saved and flagged (overdrawWarning).
 */
export function sourceBins(bins: SiteBin[], materialId: string): SiteBin[] {
  return bins.filter(({ bin }) => bin.material !== null && (!materialId || bin.material.id === materialId));
}

/** Bins a count can be recorded against: anything assigned, or unassigned (to assign it). */
export function countableBins(bins: SiteBin[], materialId: string): SiteBin[] {
  return bins.filter(({ bin }) => !materialId || !bin.material || bin.material.id === materialId);
}

/**
 * Bins stock can arrive in. A transfer or return needs the bin to be unassigned or already hold
 * this material. A receipt may also re-assign a bin that is completely empty.
 */
export function arrivalBins(
  bins: SiteBin[],
  materialId: string,
  opts: { allowEmptyReassign: boolean; excludeBinId?: string },
): SiteBin[] {
  return bins.filter(({ bin }) => {
    if (opts.excludeBinId && bin.id === opts.excludeBinId) return false;
    if (!materialId || !bin.material || bin.material.id === materialId) return true;
    return opts.allowEmptyReassign && bin.quantity === 0;
  });
}

export interface ValidationContext {
  bins: SiteBin[];
  canManageCost: boolean;
  /** Cost on file for the receiving bin / material, when there is one. */
  costOnFile: number | null;
}

export type AdjustErrors = Partial<Record<keyof AdjustForm, string>>;

/** Strict numeric parse: blank and junk are NaN, never 0. */
export function parseNumber(text: string): number {
  const trimmed = text.trim();
  return trimmed === '' ? NaN : Number(trimmed);
}

function hasAtMostDecimals(value: number, places: number): boolean {
  const scale = 10 ** places;
  return Math.abs(value * scale - Math.round(value * scale)) < 1e-6;
}

function findBin(bins: SiteBin[], id: string): SiteBin | undefined {
  return bins.find(({ bin }) => bin.id === id);
}

/** Everything wrong with the form, keyed by field. Empty means it can be saved. */
export function validateAdjust(type: MovementType, form: AdjustForm, ctx: ValidationContext): AdjustErrors {
  const spec = TYPE_SPEC[type];
  const errors: AdjustErrors = {};

  const from = findBin(ctx.bins, form.fromBin);
  const to = findBin(ctx.bins, form.toBin);

  if (spec.fromLabel && !from) errors.fromBin = `${spec.fromLabel} is required`;
  if (spec.toLabel && !to) errors.toBin = `${spec.toLabel} is required`;
  if (type === 'TRANSFER' && from && to && from.bin.id === to.bin.id) {
    errors.toBin = 'From and To location must be different';
  }
  if (!form.material) errors.material = 'Material is required';

  if (from && form.material && from.bin.material && from.bin.material.id !== form.material) {
    errors.fromBin = `${from.label} holds a different material`;
  }

  if (spec.quantity) {
    const quantity = parseNumber(form.quantity);
    if (Number.isNaN(quantity)) errors.quantity = 'Quantity is required';
    else if (quantity <= 0) errors.quantity = 'Quantity must be greater than zero';
    else if (!hasAtMostDecimals(quantity, 3)) errors.quantity = 'Use at most 3 decimal places';
  }

  if (spec.task === 'required' && !form.task) errors.task = 'Task is required';
  if (spec.vendor && !form.vendor) errors.vendor = 'Vendor is required';

  if (spec.unitCost) {
    const cost = parseNumber(form.unitCost);
    if (Number.isNaN(cost)) {
      errors.unitCost = ctx.canManageCost || ctx.costOnFile !== null
        ? 'Unit cost is required'
        : 'No unit cost is on file. Ask a materials controller to set one.';
    } else if (cost < 0) errors.unitCost = 'Unit cost cannot be negative';
    else if (!ctx.canManageCost && (ctx.costOnFile === null || cost !== ctx.costOnFile)) {
      errors.unitCost = 'Only a materials controller can change the unit cost';
    }
  }

  if (spec.counted) {
    const counted = parseNumber(form.counted);
    if (Number.isNaN(counted)) errors.counted = 'Counted quantity is required';
    else if (counted < 0) errors.counted = 'Counted quantity cannot be negative';
    else if (!hasAtMostDecimals(counted, 3)) errors.counted = 'Use at most 3 decimal places';
  }

  if (!form.reason.trim()) errors.reason = 'Context / reason is required';
  else if (form.reason.trim().length > 1000) errors.reason = 'Keep this under 1000 characters';

  return errors;
}

/**
 * Taking more than the ledger says is there. Not an error (MT-V06): the material has gone, so the
 * movement is saved and flagged and a count corrects the level. Null when there is nothing to say.
 */
export function overdrawWarning(type: MovementType, form: AdjustForm, bins: SiteBin[]): string | null {
  if (!TYPE_SPEC[type].drawsDown) return null;
  const from = findBin(bins, form.fromBin);
  const quantity = parseNumber(form.quantity);
  if (!from || Number.isNaN(quantity) || quantity <= from.bin.quantity) return null;
  const unit = from.bin.material?.unitOfMeasure;
  return `Only ${formatQuantity(from.bin.quantity, unit)} on hand. This takes ${from.label} below zero; it will be saved and flagged for a count.`;
}

/** Turn a validated form into the request body. Throws if the form was not validated. */
export function buildPayload(type: MovementType, form: AdjustForm, bins: SiteBin[] = []): MovementPayload {
  const id = (value: string) => Number(value);
  const reason = form.reason.trim();
  switch (type) {
    case 'CONSUME':
      return {
        type,
        reason,
        fromBinId: id(form.fromBin),
        quantity: parseNumber(form.quantity),
        taskId: id(form.task),
      };
    case 'DISPOSAL':
      return { type, reason, fromBinId: id(form.fromBin), quantity: parseNumber(form.quantity) };
    case 'TRANSFER':
      return {
        type,
        reason,
        fromBinId: id(form.fromBin),
        toBinId: id(form.toBin),
        quantity: parseNumber(form.quantity),
      };
    case 'RECEIPT':
      return {
        type,
        reason,
        toBinId: id(form.toBin),
        materialId: id(form.material),
        quantity: parseNumber(form.quantity),
        vendorId: id(form.vendor),
        unitCost: parseNumber(form.unitCost),
      };
    case 'COUNT':
      return {
        type,
        reason,
        binId: id(form.fromBin),
        materialId: id(form.material),
        method: form.method,
        countedQuantity: parseNumber(form.counted),
        ...versionOf(bins, form.fromBin),
      };
    case 'RETURN':
      return {
        type,
        reason,
        toBinId: id(form.toBin),
        materialId: id(form.material),
        quantity: parseNumber(form.quantity),
        ...(form.task ? { taskId: id(form.task) } : {}),
      };
  }
}

function versionOf(bins: SiteBin[], binId: string): { expectedVersion?: number } {
  const version = findBin(bins, binId)?.bin.ledgerVersion;
  return typeof version === 'number' ? { expectedVersion: version } : {};
}

/** "298 m3 → 281.5 m3 (−16.5)", or "298 m3 → —" until a figure is typed. */
export function expectedVsCounted(expected: number, countedText: string, unit: string | null | undefined): string {
  const start = formatQuantity(expected, unit);
  const counted = parseNumber(countedText);
  if (Number.isNaN(counted) || counted < 0) return `${start} → —`;
  const delta = Math.round((counted - expected) * 1000) / 1000;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
  return `${start} → ${formatQuantity(counted, unit)} (${sign}${formatQuantity(Math.abs(delta), null)})`;
}

/** One-line confirmation shown after a save. */
export function describeMovement(
  type: MovementType,
  quantity: number,
  unit: string | null,
  expected: number | null,
): string {
  const amount = formatQuantity(quantity, unit);
  switch (type) {
    case 'CONSUME':
      return `Consumed ${amount}.`;
    case 'TRANSFER':
      return `Transferred ${amount}.`;
    case 'RECEIPT':
      return `Received ${amount}.`;
    case 'DISPOSAL':
      return `Disposed of ${amount}.`;
    case 'RETURN':
      return `Returned ${amount}.`;
    case 'COUNT':
      return expected === null
        ? `Counted ${amount}.`
        : `Counted ${amount} (was ${formatQuantity(expected, unit)}).`;
  }
}

export type LevelTone = 'ok' | 'low' | 'high' | 'plain';

export interface BinLevel {
  /** "298 m3", or "No material". */
  label: string;
  /** 0–100 fill for the bar, or null when there is no min or max to measure against. */
  percent: number | null;
  /** 0–100 position of the min tick on the bar, or null when there is no min (or no bar). */
  minPercent: number | null;
  tone: LevelTone;
  /** "min 100 m3 · max 500 m3" or null. */
  band: string | null;
}

/**
 * With only a minimum set there is no natural right-hand end for the bar, so it is drawn to
 * 130% of whichever is larger, the on-hand figure or the minimum. That keeps the min tick and
 * the fill both on the bar, with a little headroom beyond them.
 */
const MIN_ONLY_HEADROOM = 1.3;

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

/** What the on-hand column shows for a bin. */
export function binLevel(bin: StorageBin): BinLevel {
  if (!bin.material) return { label: 'No material', percent: null, minPercent: null, tone: 'plain', band: null };
  const unit = bin.material.unitOfMeasure;
  const label = formatQuantity(bin.quantity, unit);
  const { minQuantity: min, maxQuantity: max } = bin;
  const band =
    min !== null || max !== null
      ? [min !== null ? `min ${formatQuantity(min, unit)}` : null, max !== null ? `max ${formatQuantity(max, unit)}` : null]
          .filter(Boolean)
          .join(' · ')
      : null;

  // The value the right-hand end of the bar stands for.
  const scale =
    max !== null && max > 0
      ? max
      : min !== null
        ? Math.max(bin.quantity, min) * MIN_ONLY_HEADROOM
        : null;
  const hasScale = scale !== null && scale > 0;
  const percent = hasScale ? clampPercent((bin.quantity / scale) * 100) : null;
  const minPercent = hasScale && min !== null ? clampPercent((min / scale) * 100) : null;

  const tone: LevelTone =
    min !== null && bin.quantity < min
      ? 'low'
      : max !== null && bin.quantity > max
        ? 'high'
        : min !== null || max !== null
          ? 'ok'
          : 'plain';
  return { label, percent, minPercent, tone, band };
}
